import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../utils/seo';

// ===== セミナー・イベント 一覧ページ 全面作り直し（Implementer C スコープ） =====
// デザイン土台: src/pages/home/page.tsx / src/pages/about/overview/page.tsx の新スタイル
//   （紺#1a2942 / 真鍮金#a38e65 / 金差し色見出し / 紺帯番号バンド NavyBand / BTN-08 2状態 /
//    細罫テーブル / リード3行+ / 装飾ディバイダー禁止）を精読し、同じ言語で再構成。
// 事実正典（実データ変更禁止・現行working tree実文のみ・創作禁止）:
//   [S1] src/pages/seminars/page.tsx（旧・6研修の実文/料金400,000円/1日3時間/10-20名/対象レベル）
//   [S2] src/pages/seminars/corporate/*/page.tsx（各研修の実タグライン・箇条・対象レベル）
//   [S3] src/pages/home/page.tsx L391-433（セミナー章のリード/研修小見出し実文）
// ここに無い研修名・料金・時間・人数は書かない。

// 紺帯番号バンド（home の NavyBand と同一文法・由来注記付き移植）。
// 帯=区切り（帯のある章境界に区切りラインを入れない #31）／帯直後に同一文言見出しを反復しない #33。
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// BTN-08（home と同一文法・2状態）
const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
const TEXT_ARROW = "mt-auto self-start inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942] tracking-[0.06em] border-b border-[#a38e65] pb-1 hover:text-[#8f7a56] transition-colors duration-300";

// [S1][S2][S3] 6研修（実データ・料金/時間/人数は全研修共通の実値、対象レベルのみ差異）
const PROGRAMS = [
  { en: 'i. Team Leadership', ja: 'チームリーダーシップ研修', tagline: '執事に学ぶリーダーシップの技術', img: '/images/generated/jba-fs-227-seminars.webp', aspect: 'aspect-[4/3]', alt: 'チームリーダーシップ研修で受講者にチーム運営の技術を指導する執事', desc: '執事が実践する「見えないリーダーシップ」を学び、チームを効果的に導く技術を習得します。部下のモチベーション向上、効果的な指示の出し方、問題解決能力の向上を図ります。', points: ['見えないリーダーシップ', 'チーム管理技術', '問題解決能力'], level: '中級〜上級', href: '/seminars/corporate/team-leadership' },
  { en: 'ii. Hospitality', ja: 'ホスピタリティ向上研修', tagline: '執事に学ぶ心からのおもてなし', img: '/images/generated/jba-fs-228-seminars-v5.webp', aspect: 'aspect-[4/3]', alt: 'ホスピタリティ向上研修でおもてなしの所作を実演する執事と受講者', desc: '執事が実践する最高峰のおもてなし精神を学び、ゲストの期待を超えるサービスを提供する技術を習得します。顧客満足度の向上と感動創造の技術を身につけます。', points: ['おもてなしの心', '顧客満足度向上', '感動創造技術'], level: '全レベル', href: '/seminars/corporate/hospitality-enhancement' },
  { en: 'iii. Communication', ja: 'コミュニケーション技術研修', tagline: '執事に学ぶ伝える力を磨く技術', img: '/images/generated/jba-fs-229-seminars-v5.webp', aspect: 'aspect-[3/4]', alt: 'コミュニケーション技術研修で傾聴と対話の演習を行う受講者', desc: '執事が実践する高度なコミュニケーションスキルを学び、傾聴力、表現力、非言語コミュニケーションの技術を習得します。円滑な人間関係の構築と効果的な意思疎通を実現します。', points: ['傾聴技術', '表現力向上', '非言語コミュニケーション'], level: '全レベル', href: '/seminars/corporate/communication-skills' },
  { en: 'iv. Crisis Management', ja: '危機管理研修', tagline: '執事に学ぶ安全を守る技術と予防力', img: '/images/generated/jba-fs-230-seminars-v6.webp', aspect: 'aspect-[3/4]', alt: '危機管理研修で緊急時対応のシミュレーションを行う受講者と執事', desc: '執事が実践する高度な危機管理能力を学び、リスクアセスメント、緊急時対応、予防策立案の技術を習得します。VIPゲストの安全を守る具体的な手法を身につけます。', points: ['リスクアセスメント', '緊急時対応', '予防策立案'], level: '中級〜上級', href: '/seminars/corporate/crisis-management' },
  { en: 'v. Cross-Cultural', ja: '異文化理解研修', tagline: '執事に学ぶ国際的なおもてなし', img: '/images/generated/jba-fs-231-seminars-v6.webp', aspect: 'aspect-[3/4]', alt: '異文化理解研修で各国のプロトコルを学ぶ受講者と国際経験豊富な執事', desc: '執事が実践する国際的なプロトコルとマナーを学び、多様な文化背景を持つゲストに適切な対応ができる技術を習得します。グローバルなサービス品質の向上を図ります。', points: ['国際マナー', '文化理解', 'プロトコル'], level: '全レベル', href: '/seminars/corporate/cross-cultural-understanding' },
  { en: 'vi. Schedule Management', ja: 'スケジュール管理研修', tagline: '執事に学ぶ時間管理の技術', img: '/images/generated/jba-fs-232-seminars-v6.webp', aspect: 'aspect-[3/4]', alt: 'スケジュール管理研修で時間配分の計画を立てる受講者', desc: '執事が実践する精密なスケジュール管理技術を学び、効率的な時間活用と優先順位の設定方法を習得します。生産性向上と業務効率化を実現します。', points: ['時間管理技術', '優先順位設定', '業務効率化'], level: '全レベル', href: '/seminars/corporate/schedule-management' },
];

const FEATURES = [
  { icon: 'ri-lightbulb-line', t: '実践的教養', d: '技術ではなく、執事としての心構えと教養を重視します。' },
  { icon: 'ri-settings-3-line', t: 'カスタマイズ可能', d: '業界・企業のニーズに合わせた完全オーダーメイドで設計します。' },
  { icon: 'ri-group-line', t: '大人数対応', d: '10-20名規模の研修に対応いたします。' },
  { icon: 'ri-building-line', t: '対象業界', d: '高級ホテル・レストラン・富裕層向けサービス業が主な対象です。' },
];

const INDUSTRIES = [
  { icon: 'ri-building-line', t: '高級ホテル・旅館' },
  { icon: 'ri-restaurant-line', t: 'ファインダイニング・高級レストラン' },
  { icon: 'ri-customer-service-line', t: '富裕層向けコンシェルジュサービス' },
  { icon: 'ri-bank-line', t: 'プライベートバンク・金融機関' },
  { icon: 'ri-home-4-line', t: '高級不動産・会員制サービス' },
  { icon: 'ri-car-line', t: '高級車ディーラー・ラグジュアリーブランド' },
];

export default function SeminarsPage() {
  useEffect(() => {
    updateSEO(seoData['/seminars']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'セミナー・イベント' }
  ];

  return (
    <Layout>
      {/* ===== ヒーロー — 全幅ビルボード型（画像+紺オーバーレイ+左寄せテキスト）。
           由来: about/overview ヒーローの全幅ビルボード文法を当ページへ適用 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-p-030-sem-listing.webp"
          alt="企業向け執事研修プログラムで受講者に所作を指導する執事の様子"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Seminars &amp; Events</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事に学ぶ、</span>
              <span className="block">企業向け研修プログラム</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              全日本執事協会では、執事の視点から学ぶ実践的な企業研修プログラムを提供しています。リーダーシップ、ホスピタリティ、VIP対応など、富裕層向けサービス業に必要な教養とマインドを養成します。技術ではなく、執事としての心構えと教養を重視した内容で、従業員のホスピタリティマインド向上と管理職のマネジメント力強化に貢献します。
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      {/* ===== I. 研修プログラムの特徴 — 4分割の細罫バンド型 = 新バリエーション①
           由来注記: home VII 会員種別「細罫テーブル調」の罫線文法を、上部2本罫の4分割特徴バンドへ翻案。
           トップ/概要に同型は無いため新造。アイコン+短見出し+一文で研修全体の性格を先出しする安心先行型。 ===== */}
      <NavyBand no="I" ja="研修プログラムの特徴" en="Why Our Training" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 sm:mb-12 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              現場の知恵を、<span className="text-[#a38e65]">明日のビジネスへ</span>
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              執事が長年の実務で磨いてきたおもてなしとマネジメントの技術は、そのまま企業の現場で力を発揮します。私たちの研修は、表面的なマニュアルではなく、人と向き合う姿勢そのものを養うことを大切にしています。以下の四つの特徴が、他にはない実践的な学びを支えています。
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t-2 border-[#1a2942]">
            {FEATURES.map((f) => (
              <div key={f.t} className="flex flex-col gap-4 border-b border-[#e2ddd2] lg:border-b-0 lg:border-r last:border-r-0 py-7 lg:px-6 first:lg:pl-0">
                <span className="inline-flex items-center justify-center w-12 h-12 border border-[#a38e65]/50 text-[#a38e65]"><i className={`${f.icon} text-2xl`}></i></span>
                <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5]">{f.t}</h4>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== II. 企業向け研修プログラム — 白リッチカード（画像上/英字eyebrow/見出し/本文/金◆箇条/細罫データ行/テキスト矢印CTA）。
           由来: home II 執事サービス白カード文法を、研修カード（対象レベル列を追加）へ拡張 ===== */}
      <NavyBand no="II" ja="企業向け研修プログラム" en="Corporate Programs" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 sm:mb-14 text-left">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              執事の技術を応用した、<span className="text-[#a38e65]">6つの研修</span>
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              執事の技術を現代のビジネスシーンに応用した実践的な研修プログラムです。各プログラムは1日3時間、10-20名対応で、料金は一律で明快です。リーダーシップから危機管理まで、貴社の課題に合わせて必要な研修をお選びいただけます。
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {PROGRAMS.map((p) => (
              <article key={p.href} className="bg-white border border-[#e2ddd2] flex flex-col group hover:border-[#a38e65]/70 transition-colors duration-300">
                <div className="relative h-56 sm:h-60 overflow-hidden">
                  <img src={p.img} alt={p.alt} className={`w-full h-full ${p.aspect === 'aspect-[3/4]' ? 'object-cover' : 'object-cover object-top'}`} />
                </div>
                <div className="p-7 sm:p-8 flex flex-col flex-1 text-left">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">{p.en}</span>
                  <h4 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl md:text-[24px] leading-[1.5] tracking-[0.02em] mb-2">{p.ja}</h4>
                  <p className="font-['Noto_Serif_JP',serif] text-[#8f7a56] text-sm mb-4">{p.tagline}</p>
                  <p className="font-['Noto_Serif_JP',serif] text-[#333] text-sm leading-[1.95] mb-6">{p.desc}</p>
                  <ul className="space-y-0 mb-6">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-3 border-b border-[#e2ddd2] py-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]"><span className="text-[#a38e65] text-[10px] mt-[7px]">◆</span><span>{pt}</span></li>
                    ))}
                  </ul>
                  <div className="grid grid-cols-2 gap-4 border-t border-[#e2ddd2] pt-5 mb-6">
                    <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">受講料（税込）</div><div className="font-['Playfair_Display',serif] text-[#1a2942] text-2xl md:text-[26px] leading-none">400,000<span className="text-base font-normal ml-0.5">円</span></div></div>
                    <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">研修時間</div><div className="font-['Noto_Serif_JP',serif] text-[#1a2942] text-lg leading-none pt-1.5">1日3時間</div></div>
                    <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">対象人数</div><div className="font-['Noto_Serif_JP',serif] text-[#1a2942] text-lg leading-none pt-1.5">10-20名</div></div>
                    <div><div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">対象レベル</div><div className="font-['Noto_Serif_JP',serif] text-[#1a2942] text-lg leading-none pt-1.5">{p.level}</div></div>
                  </div>
                  <a href={p.href} className={TEXT_ARROW}>詳細を見る <span className="text-[#a38e65]">→</span></a>
                </div>
              </article>
            ))}
          </div>

          {/* 研修プログラム比較表 — 細罫テーブル（由来: home VII 会員種別比較テーブル文法） */}
          <div className="mt-14 sm:mt-16">
            <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] mb-6">研修プログラム比較表</h3>
            <div className="border-t-2 border-[#1a2942] overflow-x-auto">
              <table className="w-full border-collapse min-w-[720px]">
                <thead>
                  <tr className="border-b border-[#e2ddd2] font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em]">
                    <th className="text-left py-4 pr-4 font-medium">プログラム名</th>
                    <th className="text-center py-4 px-4 font-medium">対象レベル</th>
                    <th className="text-center py-4 px-4 font-medium">研修時間</th>
                    <th className="text-center py-4 px-4 font-medium">受講人数</th>
                    <th className="text-right py-4 pl-4 font-medium">受講料（税込）</th>
                  </tr>
                </thead>
                <tbody className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638]">
                  {PROGRAMS.map((p) => (
                    <tr key={p.href} className="border-b border-[#e2ddd2]">
                      <td className="py-4 pr-4 text-[#1a2942] font-medium">{p.ja}</td>
                      <td className="py-4 px-4 text-center">{p.level}</td>
                      <td className="py-4 px-4 text-center">1日3時間</td>
                      <td className="py-4 px-4 text-center">10-20名</td>
                      <td className="py-4 pl-4 text-right font-['Playfair_Display',serif] text-[#1a2942] text-lg">400,000<span className="text-xs text-[#8f7a56] ml-0.5">円</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ===== III. 完全カスタマイズ — 分割エディトリアル（画像半分+テキスト半分）+ 対象業界の細罫グリッド。
           由来: home V セミナー章の分割エディトリアル文法を当ページのカスタマイズ節へ適用 ===== */}
      <NavyBand no="III" ja="貴社に合わせたカスタマイズ" en="Customization" />
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 items-stretch border border-[#e2ddd2] overflow-hidden mb-12 sm:mb-16">
            <div className="relative min-h-[300px] lg:min-h-[460px] bg-black order-1">
              <img src="/images/generated/jba-interior-chair-02.webp" alt="応接のチェスターフィールドチェア" className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <div className="order-2 flex flex-col justify-center px-8 sm:px-12 lg:px-14 py-12 sm:py-14 text-left">
              <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-4">Tailored Program</span>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
                課題に特化した、<span className="text-[#a38e65]">完全オーダーメイド</span>
              </h3>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-5">
                上記のプログラムを組み合わせたり、貴社の課題に特化した内容にカスタマイズすることも可能です。業務特性や従業員のレベルに合わせた最適なプログラムを設計いたします。
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
                まずは現場のお悩みをお聞かせください。研修の目的と対象者を丁寧に伺い、限られた時間で最大の効果が生まれる構成をご提案します。
              </p>
              <div className="mt-auto">
                <a href="/contact" className={BTN08_ON_LIGHT}>研修のご相談・お問い合わせ</a>
              </div>
            </div>
          </div>

          <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl leading-[1.5] mb-6">主な対象業界</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-[#e2ddd2]">
            {INDUSTRIES.map((ind) => (
              <div key={ind.t} className="flex items-center gap-4 border-b border-[#e2ddd2] py-5 lg:px-5 first:lg:pl-0">
                <i className={`${ind.icon} text-2xl text-[#a38e65] shrink-0`}></i>
                <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#1a2942]">{ind.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 締めCTAパネル（紺・由来: home 締めCTAパネル）===== */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 md:p-14 text-center">
            <h3 className="jba-display font-medium text-2xl sm:text-3xl text-[#f8f6f0] tracking-[0.04em]">貴社に最適な研修プログラムをご提案いたします</h3>
            <div className="w-11 h-px bg-[#a38e65] mx-auto mt-4 mb-6"></div>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] mb-8 max-w-2xl mx-auto leading-[1.95]">
              業界特性や企業課題に合わせた完全オーダーメイドの研修プログラムを設計いたします。まずはお気軽にご相談ください。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <a href="/contact" className={BTN08_ON_NAVY}>研修のご相談・お問い合わせ</a>
              <a href="/about/overview" className={BTN08_ON_NAVY}>協会について</a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
